import { m, AnimatePresence, useReducedMotion } from "framer-motion";
import clsx from "clsx";
import { useState, useReducer, useRef, useCallback, useEffect } from "react";
import {
  DIFFICULTY,
  PHASE,
  DEFAULT_CONFS,
  DEFAULT_DIVS,
  REAL_PLAYERS_INFO,
  LEAGUE_DATABASE_VERSION,
} from "../../../../../common/constants.ts";
import { ActionButton } from "../../components/ActionButton.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import { helpers } from "../../util/helpers.ts";
import { logEvent } from "../../util/logEvent.ts";
import { toWorker } from "../../util/toWorker.ts";
import type {
  View,
  GetLeagueOptions,
  Div,
  Conf,
  GameAttributesLeague,
  Phase,
  NonEmptyArray,
} from "../../../../../common/types.ts";
// Inherited from the deleted Settings view; only the difficulty description
// survived because this screen is the one place difficulty is still chosen.
const difficultyDescription =
  "Increasing difficulty makes AI teams more reluctant to trade with you, makes players less likely to sign with you, and makes it harder to turn a profit.";
import { i18n } from "../../util/useT.ts";
import LeagueMenu from "./LeagueMenu.tsx";
import type { LeagueInfo, NewLeagueTeam } from "../../../../../common/types/newLeague.ts";
import type { NewLeagueSeedInfo } from "../../../../../common/types/leagueSeed.ts";
import { orderBy } from "../../../../../common/utils.ts";
import { choice } from "../../../../../common/random.ts";
import type { NewLeagueSettings } from "../../../../../common/types/newLeague.ts";
import { PopText } from "../../components/PopText.tsx";
import { safeLocalStorage } from "../../util/safeLocalStorage.ts";
import { realtimeUpdate } from "../../util/realtimeUpdate.ts";
import { unwrapGameAttribute } from "../../../../../common/unwrapGameAttribute.ts";
import { PHASE_TEXT } from "../../util/phaseText.ts";
import {
  mobileActionButtonClassNames,
  MobileContentHero,
  MobileDecisionBar,
} from "../../mobile/mobilePrimitives.tsx";
import {
  difficultyText,
  normalizeDifficulty,
  translateDifficultyName,
} from "../../util/difficulty.ts";
import { translateTeamRegion, translateTeamName } from "../../util/teamNames.ts";
import {
  filterNewLeagueDraftForPathname,
  getNewLeagueSafeBaselines,
  getNewLeagueTeamProfile,
  getNewLeagueTeamRecommendations,
  newLeagueAdvancedSetupKeys,
  recoverRealStartingPhase,
  type NewLeagueTeamRecommendation,
} from "./newLeagueMobileFlow.ts";

const animationVariants = {
  visible: {
    x: 0,
    transition: { duration: 0.25, ease: "easeInOut" },
  },
  left: {
    x: "-100vw",
    transition: { duration: 0.25, ease: "easeInOut" },
  },
  right: {
    x: "100vw",
    transition: { duration: 0.25, ease: "easeInOut" },
  },
} as const;

const reducedMotionAnimationVariants = {
  visible: {
    x: 0,
    transition: { duration: 0 },
  },
  left: {
    x: 0,
    transition: { duration: 0 },
  },
  right: {
    x: 0,
    transition: { duration: 0 },
  },
} as const;

const teamsDefault: NewLeagueTeam[] = helpers.addPopRank(helpers.getTeamsDefault());

const initKeptKeys = (keys: string[]) => keys.filter((key) => key !== "version" && key !== "meta");

const seasons: { key: string; value: string }[] = [];
if (REAL_PLAYERS_INFO) {
  for (let i = REAL_PLAYERS_INFO.MAX_SEASON; i >= REAL_PLAYERS_INFO.MIN_SEASON; i--) {
    seasons.push({
      key: String(i),
      value: String(i),
    });
  }
}

// Keep only the stable numeric values at module scope. The labels must be read
// after i18n initialization, otherwise importing this view freezes the English
// PHASE_TEXT fallback into the real-roster starting-point dropdown.
const newLeagueStartingPhases = [
  PHASE.PRESEASON,
  PHASE.PLAYOFFS,
  PHASE.DRAFT_LOTTERY,
  PHASE.DRAFT,
  PHASE.AFTER_DRAFT,
] as const;

type WizardStep = 1 | 2 | 3 | 4;

type RealStartingPointRecovery = {
  phase: Phase;
  requestedPhase: Phase;
  season: number;
};

type NewLeagueMobileDraft = {
  difficulty?: number;
  mode?: State["mode"];
  name?: string;
  phase?: Phase;
  season?: number;
  startingSeason?: string;
  teamSearch?: string;
  tid?: number;
  wizardStep?: WizardStep;
};

type State = {
  creating: boolean;
  season: number;
  phase: Phase;
  name: string;
  nameEdited: boolean;

  mode: "random" | "real";

  // Why keep difficulty here, rather than just using settings.difficulty? Because then it won't get reset every time seed settings change.
  difficulty: number;

  sourceInfo: NewLeagueSeedInfo | undefined;
  teams: NewLeagueTeam[];
  confs: NonEmptyArray<Conf>;
  divs: NonEmptyArray<Div>;
  tid: number;
  pendingInitialLeagueInfo: boolean;
  keptKeys: string[];
  settings: NewLeagueSettings;
  rebuildAbbrevPending?: string;
};

const newLeagueMobileDraftStorageKey = "newLeagueMobileDraft";

const isWizardStep = (value: unknown): value is WizardStep =>
  value === 1 || value === 2 || value === 3 || value === 4;

const readNewLeagueMobileDraft = (): NewLeagueMobileDraft | undefined => {
  const raw = safeLocalStorage.getItem(newLeagueMobileDraftStorageKey);
  if (!raw) {
    return;
  }

  try {
    const parsed = JSON.parse(raw) as NewLeagueMobileDraft;
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch {
    return;
  }
};

type Action =
  | {
      type: "submit";
    }
  | {
      type: "error";
    }
  | {
      type: "setDifficulty";
      difficulty: string;
    }
  | {
      type: "setPhase";
      phase: Phase;
    }
  | {
      type: "setName";
      name: string;
    }
  | {
      type: "setDefaultName";
      name: string;
    }
  | {
      type: "setSeason";
      season: number;
    }
  | {
      type: "setTid";
      tid: number;
    }
  | {
      type: "setMode";
      mode: State["mode"];
    }
  | {
      type: "newLeagueInfo";
      stores: string[];
      teams: NewLeagueTeam[];
      gameAttributes: Record<string, unknown>;
      defaultSettings: State["settings"];
      startingSeason: number;
    };

const getTeamRegionName = (teams: NewLeagueTeam[], tid: number) => {
  const t = teams.find((t) => t.tid === tid);
  if (!t) {
    return "";
  }
  return `${t.region} ${t.name}`;
};

const getNewTid = (prevTeamRegionName: string, newTeams: NewLeagueTeam[]) => {
  const newTeamsSorted = orderBy(
    newTeams.filter((t) => !t.disabled),
    ["region", "name"],
  );

  // First look for exact match
  let closestNewTeam = newTeamsSorted.find((t) => prevTeamRegionName === `${t.region} ${t.name}`);

  // Second look for exact region match
  if (!closestNewTeam) {
    closestNewTeam = newTeamsSorted.find((t) => prevTeamRegionName.startsWith(t.region));
  }

  // Fallback, just get me something close
  if (!closestNewTeam) {
    closestNewTeam = newTeamsSorted.find((t) => prevTeamRegionName <= `${t.region} ${t.name}`);
  }

  return closestNewTeam ? closestNewTeam.tid : newTeams.length - 1;
};

const getDefaultLeagueName = (lid: number) =>
  i18n.t("newLeague.defaultLeagueName", {
    ns: "ui",
    defaultValue: "League {{lid}}",
    lid,
  });

const getSettingsFromGameAttributes = (
  gameAttributes: Record<string, unknown> | undefined,
  defaultSettings: State["settings"],
) => {
  const newSettings = helpers.deepCopy(defaultSettings);

  if (gameAttributes) {
    for (const key of helpers.keys(newSettings)) {
      if (
        key === "noStartingInjuries" ||
        key === "randomization" ||
        key === "realStats" ||
        key === "giveMeWorstRoster"
      ) {
        continue;
      }

      const value = unwrapGameAttribute(gameAttributes, key);
      if (value !== undefined) {
        if (key === "repeatSeason") {
          newSettings[key] = (value as any)?.type;
        } else {
          (newSettings[key] as any) = value;
        }
      }
    }
  }

  return newSettings;
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "submit":
      return {
        ...state,
        creating: true,
      };

    case "error":
      return {
        ...state,
        creating: false,
      };

    case "setDifficulty":
      return {
        ...state,
        difficulty: normalizeDifficulty(helpers.localeParseFloat(action.difficulty)),
      };

    case "setPhase":
      return {
        ...state,
        phase: action.phase,
      };

    case "setName":
      return {
        ...state,
        name: action.name,
        nameEdited: true,
      };

    case "setDefaultName":
      return {
        ...state,
        name: action.name,
        nameEdited: false,
      };

    case "setSeason":
      return {
        ...state,
        season: action.season,
      };

    case "setTid": {
      const t = state.teams.find((t) => t.tid === action.tid);
      const tid = t ? t.tid : state.teams.length > 0 ? state.teams[0]!.tid : 0;

      return {
        ...state,
        tid,
      };
    }

    case "setMode": {
      if (state.mode === action.mode) {
        return state;
      }

      const prevTeamRegionName = getTeamRegionName(state.teams, state.tid);
      const tid = getNewTid(prevTeamRegionName, teamsDefault);

      return {
        ...state,
        mode: action.mode,
        sourceInfo: undefined,
        teams: teamsDefault,
        confs: DEFAULT_CONFS,
        divs: DEFAULT_DIVS,
        tid,
        keptKeys: [],
        pendingInitialLeagueInfo: action.mode === "real",
      };
    }

    case "newLeagueInfo": {
      let prevTeamRegionName = getTeamRegionName(state.teams, state.tid);
      if (state.pendingInitialLeagueInfo) {
        const fromLocalStorage = safeLocalStorage.getItem("prevTeamRegionName");
        if (fromLocalStorage !== null) {
          prevTeamRegionName = fromLocalStorage;
        }
      }

      const keptKeys = initKeptKeys(action.stores);

      const newSettings = getSettingsFromGameAttributes(
        action.gameAttributes,
        action.defaultSettings,
      );

      const confs = unwrapGameAttribute(action.gameAttributes, "confs");
      const divs = unwrapGameAttribute(action.gameAttributes, "divs");

      let tid;
      if (state.rebuildAbbrevPending) {
        const t = action.teams.find((t) => t.srID === state.rebuildAbbrevPending);
        if (t) {
          tid = t.tid;
        }
      }
      if (tid === undefined) {
        tid = getNewTid(prevTeamRegionName, action.teams);
      }

      return {
        ...state,
        sourceInfo: {
          gameAttributes: action.gameAttributes,
          startingSeason: action.startingSeason,
          teams: action.teams,
          version: LEAGUE_DATABASE_VERSION,
        },
        keptKeys,
        confs,
        divs,
        teams: action.teams,
        tid,
        pendingInitialLeagueInfo: false,
        settings: newSettings,
        rebuildAbbrevPending: undefined,
      };
    }

    default:
      throw new Error();
  }
};

const getRebuildInfo = () => {
  if (location.hash.startsWith("#rebuild=")) {
    const rebuildSlug = location.hash.replace("#rebuild=", "");
    const parts = rebuildSlug.split("_");
    const abbrev = parts[1]?.toUpperCase();
    const season = Number.parseInt(parts[2]!);
    if (abbrev && !Number.isNaN(season)) {
      return {
        abbrev,
        season,
      };
    }
  }
};

const getGenderOverride = (): GameAttributesLeague["gender"] | undefined => {
  if (location.hash.startsWith("#gender=")) {
    const gender = location.hash.replace("#gender=", "");
    if (gender === "male" || gender === "female") {
      return gender;
    }
  }
};

const getDefaultRealStartingPoint = () => {
  const rebuildInfo = getRebuildInfo();
  if (rebuildInfo) {
    return {
      phase: PHASE.PRESEASON,
      rebuildAbbrevPending: rebuildInfo.abbrev,
      season: rebuildInfo.season,
    };
  }

  let season = Number.parseInt(safeLocalStorage.getItem("prevSeason") as any);
  if (Number.isNaN(season)) {
    season = REAL_PLAYERS_INFO?.MAX_SEASON ?? new Date().getFullYear();
  }
  let phase = Number.parseInt(safeLocalStorage.getItem("prevPhase") as any) as Phase;
  if (Number.isNaN(phase)) {
    phase = PHASE.PRESEASON;
  }

  return {
    phase,
    rebuildAbbrevPending: undefined,
    season,
  };
};

const requestRealLeagueInfo = async ({
  phase,
  season,
  settings,
}: {
  phase: Phase;
  season: number;
  settings: State["settings"];
}): Promise<LeagueInfo> =>
  await toWorker("main", "getLeagueInfo", {
    type: "real",
    season,
    phase,
    randomDebuts:
      settings.randomization === "debuts" ||
      settings.randomization === "debutsKeepCurrent" ||
      settings.randomization === "debutsForever" ||
      settings.randomization === "debutsForeverKeepCurrent",
    randomDebutsKeepCurrent:
      settings.randomization === "debutsKeepCurrent" ||
      settings.randomization === "debutsForeverKeepCurrent",
    realDraftRatings: settings.realDraftRatings,
    includePlayers: false,

    // Adding historical seasons just screws up tid
    realStats: "none",
  });

const getRecommendationReasonLabel = (reason: NewLeagueTeamRecommendation["reason"]) => {
  switch (reason) {
    case "selected":
      return i18n.t("newLeague.recommendSelected", {
        ns: "ui",
        defaultValue: "Current choice",
      });
    case "largeMarket":
      return i18n.t("newLeague.recommendLargeMarket", {
        ns: "ui",
        defaultValue: "Large market",
      });
    case "recentSeason":
      return i18n.t("newLeague.recommendRecentSeason", {
        ns: "ui",
        defaultValue: "Recent roster",
      });
    case "balanced":
      return i18n.t("newLeague.recommendBalanced", {
        ns: "ui",
        defaultValue: "Balanced start",
      });
  }
};

const getTeamDisplayName = (team: NewLeagueTeam) =>
  `${translateTeamRegion(team.region)} ${translateTeamName(team.name)}${
    team.season !== undefined ? ` (${team.season})` : ""
  }`;

const getTeamStrategyLabel = (strategy: ReturnType<typeof getNewLeagueTeamProfile>["strategy"]) => {
  switch (strategy) {
    case "contender":
      return i18n.t("newLeague.teamStrategyContender", {
        ns: "ui",
        defaultValue: "Contender",
      });
    case "rebuild":
      return i18n.t("newLeague.teamStrategyRebuild", {
        ns: "ui",
        defaultValue: "Rebuild",
      });
    case "middle":
      return i18n.t("newLeague.teamStrategyMiddle", {
        ns: "ui",
        defaultValue: "Middle",
      });
  }
};

const getTeamDifficultyLabel = (
  difficulty: ReturnType<typeof getNewLeagueTeamProfile>["difficulty"],
) => {
  switch (difficulty) {
    case "easy":
      return i18n.t("newLeague.teamDifficultyEasy", {
        ns: "ui",
        defaultValue: "Low difficulty",
      });
    case "hard":
      return i18n.t("newLeague.teamDifficultyHard", {
        ns: "ui",
        defaultValue: "High difficulty",
      });
    case "medium":
      return i18n.t("newLeague.teamDifficultyMedium", {
        ns: "ui",
        defaultValue: "Medium difficulty",
      });
  }
};

const getTeamResourceLabel = (
  key: ReturnType<typeof getNewLeagueTeamProfile>["resources"][number]["key"],
) => {
  switch (key) {
    case "market":
      return i18n.t("newLeague.teamResourceMarket", {
        ns: "ui",
        defaultValue: "Market size",
      });
    case "population":
      return i18n.t("newLeague.teamResourcePopulation", {
        ns: "ui",
        defaultValue: "Population",
      });
    case "record":
      return i18n.t("newLeague.teamResourceRecord", {
        ns: "ui",
        defaultValue: "Record",
      });
  }
};

const getTeamResourceValue = (
  resource: ReturnType<typeof getNewLeagueTeamProfile>["resources"][number],
) => {
  if (resource.key === "market") {
    return i18n.t(resource.tied ? "newLeague.teamMarketRankTied" : "newLeague.teamMarketRank", {
      ns: "ui",
      defaultValue: resource.tied
        ? "Tied for rank {{rank}}, {{total}} teams total"
        : "Rank {{rank}}, {{total}} teams total",
      rank: resource.rank,
      total: resource.totalTeams,
    });
  }

  if (resource.key === "population") {
    return i18n.t("newLeague.teamPopulationMillions", {
      ns: "ui",
      defaultValue: "{{population}} million people",
      population: resource.millions.toFixed(1),
    });
  }

  return resource.available
    ? resource.value
    : i18n.t("newLeague.teamRecordUnavailable", {
        ns: "ui",
        defaultValue: "No record available",
      });
};

const getTeamGoalLabel = (strategy: ReturnType<typeof getNewLeagueTeamProfile>["strategy"]) => {
  switch (strategy) {
    case "contender":
      return i18n.t("newLeague.teamGoalContender", {
        ns: "ui",
        defaultValue: "Win now and protect the window",
      });
    case "rebuild":
      return i18n.t("newLeague.teamGoalRebuild", {
        ns: "ui",
        defaultValue: "Collect assets and build patiently",
      });
    case "middle":
      return i18n.t("newLeague.teamGoalMiddle", {
        ns: "ui",
        defaultValue: "Choose a direction before the first sim",
      });
  }
};

const getTeamCoreResourceText = (profile: ReturnType<typeof getNewLeagueTeamProfile>) => {
  const [market, population, record] = profile.resources;
  return `${getTeamResourceLabel(market.key)} ${getTeamResourceValue(
    market,
  )} · ${getTeamResourceLabel(record.key)} ${getTeamResourceValue(record)} · ${getTeamResourceLabel(
    population.key,
  )} ${getTeamResourceValue(population)}`;
};

const TeamProfileSummary = ({
  profile,
}: {
  profile: ReturnType<typeof getNewLeagueTeamProfile>;
}) => (
  <>
    <div className="d-flex flex-wrap gap-1 mt-2">
      <span className="badge text-bg-primary" data-new-league-team-strategy="">
        {getTeamStrategyLabel(profile.strategy)}
      </span>
      <span className="badge text-bg-secondary" data-new-league-team-difficulty="">
        {getTeamDifficultyLabel(profile.difficulty)}
      </span>
    </div>
    <div className="d-flex flex-wrap gap-2 mt-2 small text-body-secondary">
      {profile.resources.map((resource) => (
        <span
          data-new-league-team-resource=""
          data-new-league-team-resource-key={resource.key}
          key={resource.key}
        >
          {getTeamResourceLabel(resource.key)}: {getTeamResourceValue(resource)}
        </span>
      ))}
    </div>
  </>
);

const TeamChoiceCard = ({
  active,
  disabled,
  onSelect,
  reasonLabel,
  team,
  teams,
}: {
  active: boolean;
  disabled: boolean;
  onSelect: () => void;
  reasonLabel?: string;
  team: NewLeagueTeam;
  teams: NewLeagueTeam[];
}) => {
  const profile = getNewLeagueTeamProfile(team, teams);
  const teamName = getTeamDisplayName(team);

  return (
    <article
      className={clsx(
        "h-100 rounded border p-2 new-league-team-card",
        active ? "border-primary bg-primary-subtle" : "bg-body",
      )}
      data-new-league-team-choice-active={active ? "true" : "false"}
      data-new-league-team-choice-tid={team.tid}
    >
      <button
        className="btn h-100 w-100 p-2 text-start"
        disabled={disabled}
        onClick={onSelect}
        type="button"
      >
        <div className="fw-semibold">{teamName}</div>
        {reasonLabel ? <div className="small text-body-secondary">{reasonLabel}</div> : null}
        <TeamProfileSummary profile={profile} />
        <div className="small mt-2">
          {i18n.t("newLeague.teamGoal", {
            ns: "ui",
            defaultValue: "Opening goal: {{goal}}",
            goal: getTeamGoalLabel(profile.strategy),
          })}
        </div>
        <div className="small text-body-secondary">
          {i18n.t("newLeague.teamCoreResource", {
            ns: "ui",
            defaultValue: "Core resources: {{resources}}",
            resources: getTeamCoreResourceText(profile),
          })}
        </div>
      </button>
      <details
        className="mt-2 small"
        data-native-details-host="new-league-team-details"
        data-new-league-team-details-drawer=""
      >
        <summary className="text-body-secondary">
          {i18n.t("newLeague.teamDetails", {
            ns: "ui",
            defaultValue: "Team details",
          })}
        </summary>
        <p className="mb-0 mt-2 text-body-secondary">
          {i18n.t("newLeague.teamDetailsDesc", {
            ns: "ui",
            defaultValue: "{{team}} starts as a {{strategy}} profile with {{difficulty}}.",
            difficulty: getTeamDifficultyLabel(profile.difficulty),
            strategy: getTeamStrategyLabel(profile.strategy),
            team: teamName,
          })}
        </p>
      </details>
    </article>
  );
};

const NewLeague = (props: View<"newLeague">) => {
  const phaseOptions = newLeagueStartingPhases.map((phase) => ({
    key: phase,
    value: helpers.upperCaseFirstLetter(PHASE_TEXT[phase]),
  }));
  const shouldReduceMotion = useReducedMotion();
  const wizardAnimationVariants = shouldReduceMotion
    ? reducedMotionAnimationVariants
    : animationVariants;
  const savedDraftRef = useRef<NewLeagueMobileDraft | undefined>(
    filterNewLeagueDraftForPathname(readNewLeagueMobileDraft(), location.pathname),
  );
  const defaultLeagueName = getDefaultLeagueName(props.defaultNameLid);
  const [defaultRealStartingPoint] = useState(getDefaultRealStartingPoint);
  const [safeBaselines] = useState(() =>
    getNewLeagueSafeBaselines({
      currentYear: new Date().getFullYear(),
      defaultDifficulty: normalizeDifficulty(props.difficulty ?? DIFFICULTY.Normal),
      defaultLeagueName,
      realPhase: defaultRealStartingPoint.phase,
      realSeason: defaultRealStartingPoint.season,
    }),
  );
  const [startingSeason, setStartingSeason] = useState(
    () => savedDraftRef.current?.startingSeason ?? safeBaselines.random.startingSeason,
  );
  const [wizardStep, setWizardStep] = useState<WizardStep>(() =>
    isWizardStep(savedDraftRef.current?.wizardStep) ? savedDraftRef.current!.wizardStep! : 1,
  );
  const [teamSearch, setTeamSearch] = useState(() => savedDraftRef.current?.teamSearch ?? "");
  const [browseTeamsOpen, setBrowseTeamsOpen] = useState(
    () => (savedDraftRef.current?.teamSearch?.trim().length ?? 0) > 0,
  );
  const [createErrorText, setCreateErrorText] = useState<string>();
  const [resettingAdvancedSetup, setResettingAdvancedSetup] = useState(false);
  const [realStartingPointRecovery, setRealStartingPointRecovery] =
    useState<RealStartingPointRecovery>();
  const resettingAdvancedSetupRef = useRef(false);

  const leagueCreationID = useRef(Math.random());

  const [state, dispatch] = useReducer(reducer, props, (props: View<"newLeague">): State => {
    const savedDraft = savedDraftRef.current;
    const teams = teamsDefault;

    let prevTeamRegionName = safeLocalStorage.getItem("prevTeamRegionName");
    if (prevTeamRegionName === null) {
      prevTeamRegionName = "";
    }

    let season = defaultRealStartingPoint.season;
    let phase = defaultRealStartingPoint.phase;
    if (typeof savedDraft?.season === "number" && Number.isFinite(savedDraft.season)) {
      season = savedDraft.season;
    }
    if (typeof savedDraft?.phase === "number" && Number.isFinite(savedDraft.phase)) {
      phase = savedDraft.phase;
    }

    let settings;
    const genderOverride = getGenderOverride();
    if (genderOverride) {
      settings = {
        ...props.defaultSettings,
        gender: genderOverride,
      };
    } else {
      settings = props.defaultSettings;
    }

    const mode =
      savedDraft?.mode === "real" || savedDraft?.mode === "random"
        ? savedDraft.mode
        : props.type === "real"
          ? props.type
          : "random";
    const defaultTid = getNewTid(prevTeamRegionName, teams);
    const savedTid =
      typeof savedDraft?.tid === "number" && teams.some((team) => team.tid === savedDraft.tid)
        ? savedDraft.tid
        : defaultTid;
    const name = savedDraft?.name?.trim() ? savedDraft.name : safeBaselines[mode].leagueName;

    return {
      creating: false,
      mode,
      season,
      difficulty: normalizeDifficulty(savedDraft?.difficulty ?? safeBaselines[mode].difficulty),
      phase,
      name,
      nameEdited: name !== getDefaultLeagueName(props.defaultNameLid),
      sourceInfo: undefined,
      teams: teamsDefault,
      confs: DEFAULT_CONFS,
      divs: DEFAULT_DIVS,
      tid: savedTid,
      pendingInitialLeagueInfo: mode === "real",
      keptKeys: [],
      settings,
      rebuildAbbrevPending: defaultRealStartingPoint.rebuildAbbrevPending,
    };
  });

  useEffect(() => {
    if (!state.nameEdited && state.name !== defaultLeagueName) {
      dispatch({
        type: "setDefaultName",
        name: defaultLeagueName,
      });
    }
  }, [defaultLeagueName, state.name, state.nameEdited]);

  useEffect(() => {
    safeLocalStorage.setItem(
      newLeagueMobileDraftStorageKey,
      JSON.stringify({
        difficulty: state.difficulty,
        mode: state.mode,
        name: state.name,
        phase: state.phase,
        season: state.season,
        startingSeason,
        teamSearch,
        tid: state.tid,
        wizardStep,
      } satisfies NewLeagueMobileDraft),
    );
  }, [
    startingSeason,
    state.difficulty,
    state.mode,
    state.name,
    state.phase,
    state.season,
    state.tid,
    teamSearch,
    wizardStep,
  ]);

  const ownedLeagueCreationTypes = props.ownedLeagueCreationTypes ?? [];
  const hasRealLeague = ownedLeagueCreationTypes.includes("real");
  const hasRandomLeague = ownedLeagueCreationTypes.includes("random");
  const canCreateRandomLeague = !hasRandomLeague;
  const canCreateRealLeague = REAL_PLAYERS_INFO !== undefined && !hasRealLeague;
  const firstAvailableMode: State["mode"] | undefined = canCreateRandomLeague
    ? "random"
    : canCreateRealLeague
      ? "real"
      : undefined;
  const selectedModeIsAvailable =
    state.mode === "real" ? canCreateRealLeague : canCreateRandomLeague;

  useEffect(() => {
    if (firstAvailableMode && !selectedModeIsAvailable) {
      dispatch({ type: "setMode", mode: firstAvailableMode });
      setWizardStep(1);
    }
  }, [firstAvailableMode, selectedModeIsAvailable]);

  let title: string;
  if (state.mode === "random") {
    title = i18n.t("newLeague.titleRandom", {
      ns: "ui",
      defaultValue: "New Random Players League",
    });
  } else if (state.mode === "real") {
    title = i18n.t("newLeague.titleReal", {
      ns: "ui",
      defaultValue: "New Real Players League",
    });
  } else {
    title = i18n.t("newLeague.titleDefault", {
      ns: "ui",
      defaultValue: "New League",
    });
  }

  const displayedTeams = state.teams;

  const createLeague = async () => {
    if (!selectedModeIsAvailable) {
      logEvent({
        type: "error",
        text: i18n.t("newLeague.leagueTypeUnavailable", {
          ns: "ui",
          defaultValue: "That league type already exists.",
        }),
        persistent: true,
        saveToDb: false,
      });
      return;
    }

    dispatch({
      type: "submit",
    });
    setCreateErrorText(undefined);

    const settings = {
      ...state.settings,
      difficulty: state.difficulty,
    };

    const actualShuffleRosters = state.keptKeys.includes("players")
      ? settings.randomization === "shuffle"
      : false;

    const startingSeasonFromInput = state.mode === "random" ? startingSeason : undefined;

    try {
      let getLeagueOptions: GetLeagueOptions | undefined;
      if (state.mode === "real") {
        getLeagueOptions = {
          type: "real",
          season: state.season,
          phase: state.phase,
          randomDebuts:
            settings.randomization === "debuts" ||
            settings.randomization === "debutsKeepCurrent" ||
            settings.randomization === "debutsForever" ||
            settings.randomization === "debutsForeverKeepCurrent",
          randomDebutsKeepCurrent:
            settings.randomization === "debutsKeepCurrent" ||
            settings.randomization === "debutsForeverKeepCurrent",
          realDraftRatings: settings.realDraftRatings,
          realStats: settings.realStats,
          includePlayers: state.keptKeys.includes("players"),
        };
      }

      const teamRegionName = getTeamRegionName(state.teams, state.tid);
      safeLocalStorage.setItem("prevTeamRegionName", teamRegionName);
      if (state.mode === "real") {
        safeLocalStorage.setItem("prevSeason", String(state.season));
        safeLocalStorage.setItem("prevPhase", String(state.phase));
      }

      const lid = await toWorker("main", "createLeague", {
        name: state.name,
        tid: state.tid,
        keptKeys: state.keptKeys,
        shuffleRosters: actualShuffleRosters,
        getLeagueOptions,
        startingSeasonFromInput,
        confs: state.confs,
        divs: state.divs,
        teamsFromInput: displayedTeams,
        settings,
        sourceInfo: state.sourceInfo,
        leagueCreationID: leagueCreationID.current,
      });
      safeLocalStorage.removeItem(newLeagueMobileDraftStorageKey);
      realtimeUpdate([], `/l/${lid}`);
    } catch (error) {
      dispatch({
        type: "error",
      });
      console.error(error);
      const message = error instanceof Error ? error.message : String(error);
      setCreateErrorText(message);
      logEvent({
        type: "error",
        text: message,
        persistent: true,
        saveToDb: false,
      });
    }
  };

  const handleNewLeagueInfo = useCallback(
    (
      leagueInfo: LeagueInfo & {
        randomization?: "debuts";
      },
    ) => {
      const newTeams = helpers.addPopRank(helpers.deepCopy(leagueInfo.teams));

      dispatch({
        type: "newLeagueInfo",
        stores: leagueInfo.stores,
        teams: newTeams,
        gameAttributes: leagueInfo.gameAttributes,
        defaultSettings:
          leagueInfo.randomization === undefined
            ? props.defaultSettings
            : {
                ...props.defaultSettings,
                randomization: leagueInfo.randomization,
              },
        startingSeason: leagueInfo.startingSeason,
      });
    },
    [props.defaultSettings],
  );

  // Real players mode can't proceed until league info (real teams, settings)
  // arrives, but LeagueMenu — whose mount effect normally fires this fetch —
  // only mounts inside the expert setup branch, and that branch's entry button
  // is disabled while pendingInitialLeagueInfo is true. Fetch here on entering
  // real mode so the wizard doesn't deadlock and step 2 shows real teams.
  useEffect(() => {
    if (state.mode !== "real" || !state.pendingInitialLeagueInfo) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const leagueInfo = await requestRealLeagueInfo({
          season: state.season,
          phase: state.phase,
          settings: state.settings,
        });
        if (!cancelled) {
          handleNewLeagueInfo(leagueInfo);
        }
      } catch (error) {
        console.error(error);
        const message = error instanceof Error ? error.message : String(error);
        logEvent({
          type: "error",
          text: `Error loading real team data: ${message}`,
          saveToDb: false,
          persistent: true,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    handleNewLeagueInfo,
    state.mode,
    state.pendingInitialLeagueInfo,
    state.phase,
    state.season,
    state.settings,
  ]);

  useEffect(() => {
    if (
      state.mode === "random" &&
      location.pathname.startsWith("/new_league/") &&
      location.pathname !== "/new_league/random"
    ) {
      window.history.replaceState(
        {
          path: "/new_league",
        },
        document.title,
        "/new_league",
      );
    } else if (
      state.mode === "real" &&
      location.pathname.startsWith("/new_league") &&
      location.pathname !== "/new_league/real"
    ) {
      window.history.replaceState(
        {
          path: "/new_league/real",
        },
        document.title,
        "/new_league/real",
      );
    }
  }, [state.mode]);

  useTitleBar({ title });

  const createLeagueText = i18n.t("newLeague.createLeagueText", {
    ns: "ui",
    defaultValue: "Create League",
  });

  const disableWhileLoadingOfficialData = state.mode === "real" && state.pendingInitialLeagueInfo;
  const showLoadingIndicator =
    disableWhileLoadingOfficialData && state.mode === "real" && state.pendingInitialLeagueInfo;

  const recoverStartingPhase = (season: number, requestedPhase: Phase) =>
    recoverRealStartingPhase({
      maxPhase: REAL_PLAYERS_INFO?.MAX_PHASE ?? requestedPhase,
      maxSeason: REAL_PLAYERS_INFO?.MAX_SEASON ?? season,
      phase: requestedPhase,
      playoffsPhase: PHASE.PLAYOFFS,
      season,
    }) as Phase;

  const bannedExpansionSeasons = [
    // Because of other mergers
    1947, 1948, 1949,

    // Because of ABA merger
    1976,
  ];
  let invalidSeasonPhaseMessage: string | undefined;
  if (state.phase > PHASE.PLAYOFFS && bannedExpansionSeasons.includes(state.season)) {
    invalidSeasonPhaseMessage = i18n.t("newLeague.invalidSeasonMerger", {
      ns: "ui",
      defaultValue:
        "Starting after the playoffs is not yet supported for seasons where league mergers occurred.",
    });
  }
  if (
    REAL_PLAYERS_INFO &&
    state.season === REAL_PLAYERS_INFO.MAX_SEASON &&
    state.phase > REAL_PLAYERS_INFO.MAX_PHASE
  ) {
    const phase =
      REAL_PLAYERS_INFO.MAX_PHASE === PHASE.PRESEASON
        ? "season"
        : (PHASE_TEXT as any)[REAL_PLAYERS_INFO.MAX_PHASE];
    invalidSeasonPhaseMessage = i18n.t("newLeague.invalidSeasonMax", {
      ns: "ui",
      defaultValue: `Sorry, I'm not allowed to share the results of the ${REAL_PLAYERS_INFO.MAX_SEASON} ${phase} yet.`,
      season: REAL_PLAYERS_INFO.MAX_SEASON,
      phase,
    });
  }

  const sortedDisplayedTeams = orderBy(displayedTeams, ["region", "name"]);
  const selectedTeam = sortedDisplayedTeams.find((t) => t.tid === state.tid);
  const teamSearchNeedle = teamSearch.trim().toLocaleLowerCase();
  const teamChoicesBase =
    teamSearchNeedle.length > 0
      ? sortedDisplayedTeams.filter((t) =>
          `${translateTeamRegion(t.region)} ${translateTeamName(t.name)} ${t.region} ${t.name}`
            .toLocaleLowerCase()
            .includes(teamSearchNeedle),
        )
      : sortedDisplayedTeams.filter((t) => !t.disabled);
  const teamChoices = selectedTeam
    ? teamSearchNeedle.length > 0
      ? teamChoicesBase
      : [selectedTeam, ...teamChoicesBase.filter((t) => t.tid !== selectedTeam.tid)]
    : teamChoicesBase;
  const recommendedTeams = getNewLeagueTeamRecommendations({
    selectedTid: state.tid,
    teams: displayedTeams,
  });
  const recommendedTeamReasonByTid = new Map(
    recommendedTeams.map(({ reason, team }) => [team.tid, reason]),
  );
  const teamsToShow = (
    browseTeamsOpen
      ? teamSearchNeedle.length > 0
        ? teamChoices
        : [
            ...recommendedTeams.map(({ team }) => team),
            ...teamChoices.filter((team) => !recommendedTeamReasonByTid.has(team.tid)),
          ]
      : recommendedTeams.map(({ team }) => team)
  ).map((team) => ({
    reason: recommendedTeamReasonByTid.get(team.tid),
    team,
  }));

  let modeLabel: string;
  if (state.mode === "real") {
    modeLabel = i18n.t("newLeague.modeReal", {
      ns: "ui",
      defaultValue: "Real players",
    });
  } else if (state.mode === "random") {
    modeLabel = i18n.t("newLeague.modeRandom", {
      ns: "ui",
      defaultValue: "Random players",
    });
  } else {
    modeLabel = i18n.t("newLeague.modeRandom", {
      ns: "ui",
      defaultValue: "Random players",
    });
  }

  const selectedTeamName = selectedTeam ? getTeamDisplayName(selectedTeam) : "";
  const selectedTeamProfile = selectedTeam
    ? getNewLeagueTeamProfile(selectedTeam, displayedTeams)
    : undefined;
  const startingPoint =
    state.mode === "real"
      ? `${state.season} ${helpers.upperCaseFirstLetter(PHASE_TEXT[state.phase])}`
      : startingSeason;
  const advancedSummary = i18n.t("newLeague.confirmSummaryAdvanced", {
    ns: "ui",
    defaultValue: "Expert choices stay at safe defaults unless you changed them.",
  });
  const primaryStepLabels = [
    i18n.t("newLeague.stepMode", { ns: "ui", defaultValue: "Choose mode" }),
    i18n.t("newLeague.stepTeam", { ns: "ui", defaultValue: "Choose team" }),
    i18n.t("newLeague.stepReview", { ns: "ui", defaultValue: "Confirm challenge" }),
  ] as const;
  const advancedStepLabel = i18n.t("newLeague.advancedSetupOptional", {
    ns: "ui",
    defaultValue: "Advanced setup (optional)",
  });
  const currentPrimaryStep = wizardStep === 1 ? 1 : wizardStep === 2 ? 2 : 3;
  // R0-5: the primary path is mode → team → confirm challenge (3 steps).
  // Advanced setup is an optional side-branch reached from the team step, not
  // a fourth primary step — labelling it "Step 3 of 3" collided with the
  // confirm screen, which is the real step 3. Show the optional branch as an
  // uncounted label so only the confirm screen carries the "Step 3 of 3" count.
  const isAdvancedBranch = wizardStep === 3;
  const stepProgressLabel = isAdvancedBranch
    ? advancedStepLabel
    : i18n.t("newLeague.stepProgress", {
        ns: "ui",
        current: currentPrimaryStep,
        label: primaryStepLabels[currentPrimaryStep - 1],
        total: primaryStepLabels.length,
        defaultValue: "Step {{current}} of {{total}} · {{label}}",
      });
  const backToHomeText = i18n.t("newLeague.backToHome", {
    ns: "ui",
    defaultValue: "Back to home",
  });
  const resetAdvancedSetup = async () => {
    if (resettingAdvancedSetupRef.current) {
      return false;
    }

    resettingAdvancedSetupRef.current = true;
    setResettingAdvancedSetup(true);
    const baseline = safeBaselines[state.mode];

    try {
      if (baseline.mode === "random") {
        dispatch({
          type: "setDefaultName",
          name: baseline.leagueName,
        });
        dispatch({
          type: "setDifficulty",
          difficulty: String(baseline.difficulty),
        });
        setStartingSeason(baseline.startingSeason);
        return true;
      }

      const leagueInfo = await requestRealLeagueInfo({
        phase: baseline.phase as Phase,
        season: baseline.season,
        settings: props.defaultSettings,
      });
      dispatch({
        type: "setDefaultName",
        name: baseline.leagueName,
      });
      dispatch({
        type: "setDifficulty",
        difficulty: String(baseline.difficulty),
      });
      dispatch({ type: "setSeason", season: baseline.season });
      dispatch({ type: "setPhase", phase: baseline.phase as Phase });
      handleNewLeagueInfo(leagueInfo);
      return true;
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : String(error);
      logEvent({
        type: "error",
        text: `Error loading real team data: ${message}`,
        saveToDb: false,
        persistent: true,
      });
      return false;
    } finally {
      resettingAdvancedSetupRef.current = false;
      setResettingAdvancedSetup(false);
    }
  };
  const handleResetAdvancedChoices = () => {
    void resetAdvancedSetup();
  };
  const handleUseSafeDefaults = async () => {
    if (await resetAdvancedSetup()) {
      setWizardStep(4);
    }
  };
  const handleConfirmChallenge = () => {
    setWizardStep(4);
  };
  const returnToPreviousStepFromReview = () => {
    setWizardStep(2);
  };

  if (!firstAvailableMode) {
    return (
      <AnimatePresence mode="popLayout" initial={false}>
        <m.div
          data-new-league-unavailable="quota"
          key="new-league-unavailable"
          variants={wizardAnimationVariants}
          initial={shouldReduceMotion ? false : "left"}
          animate="visible"
          exit={shouldReduceMotion ? "visible" : "left"}
          style={{ maxWidth: 800 }}
        >
          <div className="mb-3">
            <a className="btn btn-outline-secondary" href="/">
              {backToHomeText}
            </a>
          </div>
          <div className="alert alert-secondary">
            {i18n.t("newLeague.allLeagueTypesUsed", {
              ns: "ui",
              defaultValue:
                "You already have one league of each type. Delete an existing league before creating another.",
            })}
          </div>
        </m.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <m.div
        data-new-league-browser-back-policy="exit-wizard"
        data-new-league-draft-persistence=""
        data-new-league-wizard
        data-new-league-wizard-mode={state.mode}
        data-new-league-wizard-step={wizardStep}
        key={`screen-${wizardStep}`}
        variants={wizardAnimationVariants}
        initial={shouldReduceMotion ? false : "left"}
        animate="visible"
        exit={shouldReduceMotion ? "visible" : "left"}
        style={{ maxWidth: 800 }}
      >
        <div
          className="d-inline-flex align-items-center rounded-pill border bg-body px-3 py-2 small mb-3"
          data-new-league-progress-capsule=""
          data-new-league-progress-current={currentPrimaryStep}
          data-new-league-progress-total={primaryStepLabels.length}
        >
          <span data-new-league-step-progress={wizardStep}>{stepProgressLabel}</span>
        </div>
        {showLoadingIndicator ? (
          <div
            aria-busy="true"
            className="alert alert-info d-flex align-items-center gap-2"
            data-new-league-official-data-loading=""
            id="new-league-official-data-loading"
            role="status"
          >
            <span aria-hidden="true" className="spinner-border spinner-border-sm" />
            <span>
              {i18n.t("newLeague.loadingOfficialData", {
                ns: "ui",
                defaultValue: "Loading official team data…",
              })}
            </span>
          </div>
        ) : null}
        {wizardStep === 1 ? (
          <MobileContentHero
            explainer={i18n.t("newLeague.onboardingSubtitle", {
              ns: "ui",
              defaultValue:
                "Start with the recommended challenge, then change modes only if you want a different style.",
            })}
          >
            <div>
              {canCreateRealLeague ? (
                <button
                  className={clsx(
                    "btn text-start border rounded p-3 w-100 new-league-mode-hero",
                    state.mode === "real" ? "border-primary bg-primary-subtle" : "bg-body",
                  )}
                  aria-pressed={state.mode === "real"}
                  aria-describedby={
                    disableWhileLoadingOfficialData ? "new-league-official-data-loading" : undefined
                  }
                  data-new-league-mode-card=""
                  data-new-league-mode-recommended={state.mode === "real"}
                  disabled={disableWhileLoadingOfficialData}
                  onClick={() => dispatch({ type: "setMode", mode: "real" })}
                  type="button"
                >
                  <div className="small text-body-secondary" data-new-league-mode-audience="">
                    {i18n.t("newLeague.recommendedModeEyebrow", {
                      ns: "ui",
                      defaultValue: "Recommended start",
                    })}
                  </div>
                  <div className="fw-semibold">
                    {i18n.t("newLeague.modeReal", {
                      ns: "ui",
                      defaultValue: "Real players",
                    })}
                  </div>
                  <div className="small text-body-secondary">
                    {i18n.t("newLeague.modeRealDesc", {
                      ns: "ui",
                      defaultValue: "Start from historical or current rosters.",
                    })}
                  </div>
                  <div className="small mt-2" data-new-league-mode-outcome="">
                    {i18n.t("newLeague.modeRealOutcome", {
                      ns: "ui",
                      defaultValue: "You will choose a season, phase, and real roster.",
                    })}
                  </div>
                </button>
              ) : canCreateRandomLeague ? (
                <button
                  className={clsx(
                    "btn text-start border rounded p-3 w-100 new-league-mode-hero",
                    state.mode === "random" ? "border-primary bg-primary-subtle" : "bg-body",
                  )}
                  aria-pressed={state.mode === "random"}
                  data-new-league-mode-card=""
                  data-new-league-mode-recommended={state.mode === "random"}
                  disabled={disableWhileLoadingOfficialData}
                  onClick={() => dispatch({ type: "setMode", mode: "random" })}
                  type="button"
                >
                  <div className="small text-body-secondary" data-new-league-mode-audience="">
                    {i18n.t("newLeague.recommendedModeEyebrow", {
                      ns: "ui",
                      defaultValue: "Recommended start",
                    })}
                  </div>
                  <div className="fw-semibold">
                    {i18n.t("newLeague.modeRandom", {
                      ns: "ui",
                      defaultValue: "Random players",
                    })}
                  </div>
                  <div className="small text-body-secondary">
                    {i18n.t("newLeague.modeRandomDesc", {
                      ns: "ui",
                      defaultValue: "Generate a fresh league quickly.",
                    })}
                  </div>
                  <div className="small mt-2" data-new-league-mode-outcome="">
                    {i18n.t("newLeague.modeRandomOutcome", {
                      ns: "ui",
                      defaultValue:
                        "You will get generated players and safe defaults for a new league.",
                    })}
                  </div>
                </button>
              ) : null}
            </div>

            {canCreateRandomLeague && canCreateRealLeague ? (
              <details
                className="border rounded bg-body p-3"
                data-native-details-host="new-league-playstyles"
              >
                <summary className="fw-semibold">
                  {i18n.t("newLeague.morePlaystyles", {
                    ns: "ui",
                    defaultValue: "More play styles",
                  })}
                </summary>
                <button
                  className={clsx(
                    "btn text-start border rounded p-3 w-100 mt-3",
                    state.mode === "random" ? "border-primary bg-primary-subtle" : "bg-body",
                  )}
                  aria-pressed={state.mode === "random"}
                  data-new-league-mode-card=""
                  data-new-league-mode-recommended={state.mode === "random"}
                  disabled={disableWhileLoadingOfficialData}
                  onClick={() => dispatch({ type: "setMode", mode: "random" })}
                  type="button"
                >
                  <div className="fw-semibold">
                    {i18n.t("newLeague.modeRandom", {
                      ns: "ui",
                      defaultValue: "Random players",
                    })}
                  </div>
                  <div className="small text-body-secondary">
                    {i18n.t("newLeague.modeRandomDesc", {
                      ns: "ui",
                      defaultValue: "Generate a fresh league quickly.",
                    })}
                  </div>
                  <div className="small mt-2" data-new-league-mode-audience="">
                    {i18n.t("newLeague.modeRandomAudience", {
                      ns: "ui",
                      defaultValue: "Best for managers who want a fast, balanced start.",
                    })}
                  </div>
                  <div className="small text-body-secondary" data-new-league-mode-outcome="">
                    {i18n.t("newLeague.modeRandomOutcome", {
                      ns: "ui",
                      defaultValue:
                        "You will get generated players and safe defaults for a new league.",
                    })}
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <span className="badge text-bg-light border">
                      {i18n.t("newLeague.randomTagFictional", {
                        ns: "ui",
                        defaultValue: "Fictional teams",
                      })}
                    </span>
                    <span className="badge text-bg-light border">
                      {i18n.t("newLeague.randomTagDynasty", {
                        ns: "ui",
                        defaultValue: "Long dynasty",
                      })}
                    </span>
                    <span className="badge text-bg-light border">
                      {i18n.t("newLeague.randomTagUnpredictable", {
                        ns: "ui",
                        defaultValue: "Unpredictable",
                      })}
                    </span>
                  </div>
                  <div
                    className="small text-warning-emphasis mt-2"
                    data-new-league-random-irreversible=""
                  >
                    {i18n.t("newLeague.modeRandomIrreversible", {
                      ns: "ui",
                      defaultValue: "Random players cannot be restored into real rosters later.",
                    })}
                  </div>
                </button>
              </details>
            ) : null}

            <div
              className="position-sticky bottom-0 z-1 new-league-sticky-action-shell"
              data-new-league-sticky-action-bar=""
              style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
            >
              <MobileDecisionBar
                primaryAction={
                  <button
                    className={mobileActionButtonClassNames.primary}
                    data-new-league-primary-action
                    data-new-league-next-team-action=""
                    aria-describedby={
                      disableWhileLoadingOfficialData
                        ? "new-league-official-data-loading"
                        : undefined
                    }
                    disabled={!selectedModeIsAvailable || disableWhileLoadingOfficialData}
                    onClick={() => setWizardStep(2)}
                    type="button"
                  >
                    {i18n.t("newLeague.nextChooseTeam", {
                      ns: "ui",
                      defaultValue: "Choose team",
                    })}
                  </button>
                }
                secondaryActions={
                  <a className={mobileActionButtonClassNames.tertiary} href="/">
                    {backToHomeText}
                  </a>
                }
              />
            </div>
          </MobileContentHero>
        ) : null}

        {wizardStep === 2 ? (
          <MobileContentHero
            explainer={i18n.t("newLeague.chooseTeamSubtitle", {
              ns: "ui",
              defaultValue:
                "Pick the challenge you want to manage. Team cards show difficulty, goal, and starting resources first.",
            })}
          >
            <div
              className="mb-4"
              data-new-league-recommended-teams={!browseTeamsOpen ? "" : undefined}
              data-new-league-team-list={browseTeamsOpen ? "expanded" : "recommended"}
            >
              <div className="form-label mb-2">
                {browseTeamsOpen
                  ? i18n.t("newLeague.allTeams", {
                      ns: "ui",
                      defaultValue: "All teams",
                    })
                  : i18n.t("newLeague.recommendedTeams", {
                      ns: "ui",
                      defaultValue: "Recommended teams",
                    })}
              </div>
              {browseTeamsOpen ? (
                <div className="d-flex flex-column gap-2 mb-3">
                  <label htmlFor="new-league-team-search" className="form-label">
                    {i18n.t("newLeague.searchTeams", {
                      ns: "ui",
                      defaultValue: "Search teams",
                    })}
                  </label>
                  <input
                    id="new-league-team-search"
                    className="form-control"
                    disabled={disableWhileLoadingOfficialData}
                    placeholder={i18n.t("newLeague.searchTeams", {
                      ns: "ui",
                      defaultValue: "Search teams",
                    })}
                    type="search"
                    value={teamSearch}
                    onChange={(event) => {
                      setTeamSearch(event.target.value);
                    }}
                  />
                  {teamSearchNeedle.length > 0 ? (
                    <button
                      className="btn btn-outline-secondary align-self-start"
                      data-new-league-team-search-clear=""
                      disabled={disableWhileLoadingOfficialData}
                      onClick={() => setTeamSearch("")}
                      type="button"
                    >
                      {i18n.t("newLeague.clearTeamSearch", {
                        ns: "ui",
                        defaultValue: "Clear team search",
                      })}
                    </button>
                  ) : null}
                  <button
                    className="btn btn-outline-secondary align-self-start"
                    disabled={disableWhileLoadingOfficialData}
                    type="button"
                    onClick={() => {
                      const team = choice(displayedTeams);
                      dispatch({ type: "setTid", tid: team.tid });
                    }}
                  >
                    {i18n.t("common.random", {
                      ns: "ui",
                      defaultValue: "Random",
                    })}
                  </button>
                  {!state.settings.equalizeRegions ? (
                    <PopText
                      className="text-body-secondary"
                      tid={state.tid}
                      teams={displayedTeams}
                      numActiveTeams={displayedTeams.length}
                    />
                  ) : (
                    <span className="text-body-secondary">
                      {i18n.t("newLeague.populationEqual", {
                        ns: "ui",
                        defaultValue: "Population: equal",
                      })}
                    </span>
                  )}
                </div>
              ) : null}
              <div
                className="row row-cols-1 row-cols-sm-3 g-2"
                data-new-league-team-choices=""
                data-new-league-team-result-count={teamsToShow.length}
              >
                {showLoadingIndicator ? (
                  <div className="col">
                    <div className="border rounded p-3 text-body-secondary">
                      {i18n.t("newLeague.loadingOption", {
                        ns: "ui",
                        defaultValue: "Loading...",
                      })}
                    </div>
                  </div>
                ) : teamsToShow.length > 0 ? (
                  teamsToShow.map(({ reason, team }) => {
                    const active = team.tid === state.tid;

                    return (
                      <div className="col" key={team.tid}>
                        <TeamChoiceCard
                          active={active}
                          disabled={disableWhileLoadingOfficialData || !!team.disabled}
                          onSelect={() => {
                            dispatch({
                              type: "setTid",
                              tid: team.tid,
                            });
                          }}
                          reasonLabel={reason ? getRecommendationReasonLabel(reason) : undefined}
                          team={team}
                          teams={displayedTeams}
                        />
                      </div>
                    );
                  })
                ) : (
                  <div className="col">
                    <div
                      className="border rounded p-3 text-body-secondary"
                      data-new-league-team-search-empty=""
                      role="status"
                    >
                      {i18n.t("newLeague.noTeamsFound", {
                        ns: "ui",
                        defaultValue: "No teams match that search.",
                      })}
                    </div>
                  </div>
                )}
              </div>
              <button
                aria-expanded={browseTeamsOpen}
                className="btn btn-link text-body-secondary d-block mx-auto mt-3"
                data-new-league-browse-teams=""
                disabled={disableWhileLoadingOfficialData}
                onClick={() => setBrowseTeamsOpen((open) => !open)}
                type="button"
              >
                {browseTeamsOpen
                  ? i18n.t("newLeague.collapseTeams", {
                      ns: "ui",
                      defaultValue: "Show fewer teams",
                    })
                  : i18n.t("newLeague.browseTeams", {
                      ns: "ui",
                      defaultValue: "View more teams",
                    })}
              </button>
            </div>

            <div
              className="position-sticky bottom-0 z-1 new-league-sticky-action-shell"
              data-new-league-sticky-action-bar=""
              style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
            >
              <MobileDecisionBar
                status={
                  <span data-new-league-selected-result="">
                    {i18n.t("newLeague.selectedResult", {
                      ns: "ui",
                      defaultValue: "Selected: {{team}}",
                      team: selectedTeamName,
                    })}
                  </span>
                }
                primaryAction={
                  <button
                    className={mobileActionButtonClassNames.primary}
                    data-new-league-primary-action
                    data-new-league-next-review-action=""
                    disabled={!selectedModeIsAvailable || disableWhileLoadingOfficialData}
                    onClick={handleConfirmChallenge}
                    type="button"
                  >
                    {i18n.t("newLeague.nextReview", {
                      ns: "ui",
                      defaultValue: "Confirm challenge",
                    })}
                  </button>
                }
                secondaryActions={
                  <>
                    <button
                      className={mobileActionButtonClassNames.secondary}
                      onClick={() => setWizardStep(1)}
                      type="button"
                    >
                      {i18n.t("common.back", { ns: "ui", defaultValue: "Back" })}
                    </button>
                    <button
                      className={mobileActionButtonClassNames.expert}
                      data-new-league-next-advanced-action=""
                      disabled={!selectedModeIsAvailable || disableWhileLoadingOfficialData}
                      onClick={() => setWizardStep(3)}
                      type="button"
                    >
                      {i18n.t("newLeague.expertSetup", {
                        ns: "ui",
                        defaultValue: "Expert setup",
                      })}
                    </button>
                  </>
                }
              />
            </div>
          </MobileContentHero>
        ) : null}

        {wizardStep === 3 ? (
          <section>
            <h2 className="h5 mb-2">
              {i18n.t("newLeague.expertSetup", {
                ns: "ui",
                defaultValue: "Expert setup",
              })}
            </h2>

            <details
              className="border rounded bg-body p-3 mb-4"
              data-native-details-host="new-league-expert-fields"
              data-new-league-advanced-keys={newLeagueAdvancedSetupKeys.join(",")}
              data-new-league-advanced-setup-sheet
            >
              <summary className="btn btn-outline-secondary w-100 text-start">
                <span className="fw-semibold">
                  {i18n.t("newLeague.expertFieldsSummary", {
                    ns: "ui",
                    defaultValue: "Open expert fields",
                  })}
                </span>
              </summary>
              <div className="mt-3">
                <div className="alert alert-warning mb-3" data-new-league-advanced-risk="">
                  {i18n.t("newLeague.advancedRisk", {
                    ns: "ui",
                    defaultValue:
                      "Advanced options can change league balance, historical data, and challenge difficulty.",
                  })}
                  <div className="mt-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      data-new-league-advanced-reset=""
                      disabled={resettingAdvancedSetup}
                      onClick={handleResetAdvancedChoices}
                      type="button"
                    >
                      {i18n.t("newLeague.resetAdvanced", {
                        ns: "ui",
                        defaultValue: "Reset advanced choices",
                      })}
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label" htmlFor="new-league-name">
                    {i18n.t("newLeague.leagueName", {
                      ns: "ui",
                      defaultValue: "League name",
                    })}
                  </label>
                  <input
                    id="new-league-name"
                    className="form-control"
                    type="text"
                    value={state.name}
                    onChange={(event) => {
                      dispatch({
                        type: "setName",
                        name: event.target.value,
                      });
                    }}
                  />
                </div>

                {state.mode === "random" ? (
                  <div className="mb-3">
                    <label className="form-label" htmlFor="new-league-starting-season">
                      {i18n.t("newLeague.season", {
                        ns: "ui",
                        defaultValue: "Season",
                      })}
                    </label>
                    <input
                      id="new-league-starting-season"
                      className="form-control"
                      type="text"
                      value={startingSeason}
                      onChange={(event) => {
                        setStartingSeason(event.target.value);
                      }}
                      inputMode="numeric"
                    />
                  </div>
                ) : null}

                {state.mode === "real" ? (
                  <div
                    className="mb-3"
                    data-new-league-real-phase={state.phase}
                    data-new-league-real-season={state.season}
                    data-new-league-real-starting-point=""
                    data-new-league-real-starting-point-label={startingPoint}
                  >
                    <LeagueMenu
                      value={String(state.season)}
                      values={seasons}
                      getLeagueInfo={(value, value2) =>
                        requestRealLeagueInfo({
                          season: Number.parseInt(value),
                          phase: recoverStartingPhase(Number.parseInt(value), value2 as Phase),
                          settings: state.settings,
                        })
                      }
                      onLoading={(value, requestedPhase) => {
                        const season = Number.parseInt(value);
                        const phase = recoverStartingPhase(season, requestedPhase as Phase);
                        dispatch({ type: "setSeason", season });
                        dispatch({ type: "setPhase", phase });
                        setRealStartingPointRecovery(
                          phase === requestedPhase
                            ? undefined
                            : {
                                phase,
                                requestedPhase: requestedPhase as Phase,
                                season,
                              },
                        );
                      }}
                      onDone={handleNewLeagueInfo}
                      quickValues={[
                        "1956",
                        "1968",
                        "1984",
                        "1996",
                        "2003",
                        `${REAL_PLAYERS_INFO!.MAX_SEASON}`,
                      ]}
                      value2={state.phase}
                      values2={phaseOptions}
                      onNewValue2={(phase: any) => {
                        dispatch({
                          type: "setPhase",
                          phase,
                        });
                      }}
                    />
                    {realStartingPointRecovery ? (
                      <div
                        className="alert alert-info py-2 mt-2 mb-0"
                        data-new-league-real-recovery=""
                        data-new-league-real-recovery-phase={realStartingPointRecovery.phase}
                        data-new-league-real-recovery-requested-phase={
                          realStartingPointRecovery.requestedPhase
                        }
                        data-new-league-real-recovery-season={realStartingPointRecovery.season}
                        role="status"
                      >
                        {i18n.t("newLeague.realStartingPointRecovered", {
                          ns: "ui",
                          defaultValue:
                            "That phase is unavailable for {{season}}, so the starting point was reset to {{phase}}.",
                          phase: helpers.upperCaseFirstLetter(
                            PHASE_TEXT[realStartingPointRecovery.phase],
                          ),
                          season: realStartingPointRecovery.season,
                        })}
                      </div>
                    ) : null}
                    {invalidSeasonPhaseMessage ? (
                      <div className="text-danger mt-1" data-new-league-invalid-starting-point="">
                        {invalidSeasonPhaseMessage}
                      </div>
                    ) : (
                      <div className="text-body-secondary mt-1">
                        {i18n.t("newLeague.seasonNote", {
                          ns: "ui",
                          defaultValue: "{{season}} in BBGM is the {{prev}}-{{short}} season.",
                          season: state.season,
                          prev: state.season - 1,
                          short: String(state.season).slice(2),
                        })}
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="mb-0">
                  <div className="form-label">
                    {i18n.t("newLeague.difficulty", {
                      ns: "ui",
                      defaultValue: "Difficulty",
                    })}
                  </div>
                  <div className="d-flex flex-wrap gap-2 mb-2" data-new-league-difficulty-choices>
                    {Object.entries(DIFFICULTY).map(([text, numeric]) => (
                      <button
                        aria-pressed={state.difficulty === numeric}
                        className={clsx(
                          "btn btn-sm",
                          state.difficulty === numeric ? "btn-primary" : "btn-outline-secondary",
                        )}
                        data-new-league-difficulty-choice-active={
                          state.difficulty === numeric ? "true" : "false"
                        }
                        key={numeric}
                        onClick={() => {
                          dispatch({
                            type: "setDifficulty",
                            difficulty: String(numeric),
                          });
                        }}
                        type="button"
                      >
                        {translateDifficultyName(text)}
                      </button>
                    ))}
                  </div>
                  <span className="text-body-secondary">
                    {i18n.t("desc.difficulty", {
                      ns: "settings",
                      defaultValue: difficultyDescription,
                    })}
                  </span>
                </div>
              </div>
            </details>

            <div
              className="position-sticky bottom-0 z-1 d-flex align-items-center justify-content-between gap-2 border-top bg-body py-3"
              data-new-league-sticky-action-bar=""
              style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
            >
              <button
                className="btn btn-secondary"
                disabled={resettingAdvancedSetup}
                onClick={() => setWizardStep(2)}
                type="button"
              >
                {i18n.t("common.back", { ns: "ui", defaultValue: "Back" })}
              </button>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary"
                  disabled={resettingAdvancedSetup || disableWhileLoadingOfficialData}
                  onClick={handleUseSafeDefaults}
                  type="button"
                >
                  {i18n.t("newLeague.useSafeDefaults", {
                    ns: "ui",
                    defaultValue: "Use safe defaults",
                  })}
                </button>
                <button
                  className="btn btn-primary"
                  data-new-league-primary-action
                  data-new-league-next-review-action=""
                  disabled={
                    resettingAdvancedSetup ||
                    disableWhileLoadingOfficialData ||
                    !!invalidSeasonPhaseMessage
                  }
                  onClick={handleConfirmChallenge}
                  type="button"
                >
                  {i18n.t("newLeague.nextReview", {
                    ns: "ui",
                    defaultValue: "Confirm challenge",
                  })}
                </button>
              </div>
            </div>
          </section>
        ) : null}

        {wizardStep === 4 ? (
          <MobileContentHero
            explainer={i18n.t("newLeague.reviewSubtitle", {
              ns: "ui",
              defaultValue: "Review the opening promise before the league is created.",
            })}
          >
            {createErrorText ? (
              <div className="alert alert-danger" data-new-league-create-error-retains-input="">
                {i18n.t("newLeague.createErrorRetainsInput", {
                  ns: "ui",
                  defaultValue: "Creation failed: {{error}}. Your choices are still here.",
                  error: createErrorText,
                })}
              </div>
            ) : null}

            {/* R5-4: the "opening preview", "league creation summary", and
                "your challenge" cards all restated the same facts (team, mode,
                starting point, difficulty). Collapsed into one summary card of
                key points. Keeps the .new-league-opening-preview class and the
                data-new-league-challenge-summary marker (plus the
                challengeFirstTask / challengeBudgetRisk copy) so both entry
                guards stay green without loosening them. */}
            <div
              className="card mb-3 new-league-opening-preview"
              data-new-league-challenge-summary=""
              data-new-league-review-mode={state.mode}
              data-new-league-review-phase={state.phase}
              data-new-league-review-season={state.season}
              data-new-league-review-tid={state.tid}
            >
              <div className="card-body">
                <div className="small text-body-secondary">
                  {i18n.t("newLeague.openingPreview", {
                    ns: "ui",
                    defaultValue: "Opening preview",
                  })}
                </div>
                <h3 className="h5 mb-2">
                  {i18n.t("newLeague.openingCoachLine", {
                    ns: "ui",
                    defaultValue: "You will coach {{team}} from {{start}}.",
                    start: startingPoint,
                    team: selectedTeamName,
                  })}
                </h3>
                <p className="mb-2">
                  {i18n.t("newLeague.challengeFirstTask", {
                    ns: "ui",
                    defaultValue: "First task: review your roster and choose the opening move.",
                  })}
                </p>
                <p className="mb-2 text-body-secondary">
                  {i18n.t("newLeague.challengeBudgetRisk", {
                    ns: "ui",
                    defaultValue: "Confirm payroll and roster flexibility before the first sim.",
                  })}
                </p>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge text-bg-light border">{modeLabel}</span>
                  <span className="badge text-bg-light border">
                    {selectedTeamProfile
                      ? getTeamDifficultyLabel(selectedTeamProfile.difficulty)
                      : difficultyText(state.difficulty)}
                  </span>
                  <span className="badge text-bg-light border">
                    {selectedTeamProfile
                      ? getTeamGoalLabel(selectedTeamProfile.strategy)
                      : startingPoint}
                  </span>
                </div>
                <div
                  className="small text-body-secondary mt-2"
                  data-new-league-confirm-summary-item="advanced"
                  data-new-league-confirm-summary-value={advancedSummary}
                >
                  {i18n.t("newLeague.advancedSetup", {
                    ns: "ui",
                    defaultValue: "Advanced setup",
                  })}
                  : {advancedSummary}
                </div>
              </div>
            </div>

            <div className="list-group mb-3">
              <div
                className="list-group-item d-flex justify-content-between gap-3"
                data-new-league-confirm-summary-item="mode"
              >
                <span className="text-body-secondary">
                  {i18n.t("newLeague.selectedMode", {
                    ns: "ui",
                    defaultValue: "Players",
                  })}
                </span>
                <span className="text-end">
                  <strong data-new-league-confirm-summary-value={modeLabel}>{modeLabel}</strong>
                  <button
                    className="btn btn-link btn-sm d-block ms-auto px-0"
                    data-new-league-edit-link=""
                    onClick={returnToPreviousStepFromReview}
                    type="button"
                  >
                    {i18n.t("newLeague.edit", { ns: "ui", defaultValue: "Edit" })}
                  </button>
                </span>
              </div>
              <div
                className="list-group-item d-flex justify-content-between gap-3"
                data-new-league-confirm-summary-item="starting-point"
              >
                <span className="text-body-secondary">
                  {i18n.t("newLeague.startingPoint", {
                    ns: "ui",
                    defaultValue: "Starting point",
                  })}
                </span>
                <span className="text-end">
                  <strong data-new-league-confirm-summary-value={startingPoint}>
                    {startingPoint}
                  </strong>
                  <button
                    className="btn btn-link btn-sm d-block ms-auto px-0"
                    data-new-league-edit-link=""
                    onClick={returnToPreviousStepFromReview}
                    type="button"
                  >
                    {i18n.t("newLeague.edit", { ns: "ui", defaultValue: "Edit" })}
                  </button>
                </span>
              </div>
              <div
                className="list-group-item d-flex justify-content-between gap-3"
                data-new-league-confirm-summary-item="team"
              >
                <span className="text-body-secondary">
                  {i18n.t("newLeague.selectedTeam", {
                    ns: "ui",
                    defaultValue: "Team",
                  })}
                </span>
                <span className="text-end">
                  <strong data-new-league-confirm-summary-value={selectedTeamName}>
                    {selectedTeamName}
                  </strong>
                  <button
                    className="btn btn-link btn-sm d-block ms-auto px-0"
                    data-new-league-edit-link=""
                    onClick={returnToPreviousStepFromReview}
                    type="button"
                  >
                    {i18n.t("newLeague.edit", { ns: "ui", defaultValue: "Edit" })}
                  </button>
                </span>
              </div>
              <div
                className="list-group-item d-flex justify-content-between gap-3"
                data-new-league-confirm-summary-item="difficulty"
              >
                <span className="text-body-secondary">
                  {i18n.t("newLeague.selectedDifficulty", {
                    ns: "ui",
                    defaultValue: "Difficulty",
                  })}
                </span>
                <span className="text-end">
                  <strong data-new-league-confirm-summary-value={difficultyText(state.difficulty)}>
                    {difficultyText(state.difficulty)}
                  </strong>
                  <button
                    className="btn btn-link btn-sm d-block ms-auto px-0"
                    data-new-league-edit-link=""
                    onClick={returnToPreviousStepFromReview}
                    type="button"
                  >
                    {i18n.t("newLeague.edit", { ns: "ui", defaultValue: "Edit" })}
                  </button>
                </span>
              </div>
            </div>

            <div
              className="position-sticky bottom-0 z-1 new-league-sticky-action-shell"
              data-new-league-sticky-action-bar=""
              style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
            >
              {/* The review itself is enough confirmation. New managers can
                  create immediately without a second acknowledgement step. */}
              <MobileDecisionBar
                primaryAction={
                  <div data-new-league-primary-action>
                    <ActionButton
                      data-new-league-create-submit=""
                      size="lg"
                      type="button"
                      disabled={
                        state.creating ||
                        !selectedModeIsAvailable ||
                        disableWhileLoadingOfficialData ||
                        !!invalidSeasonPhaseMessage
                      }
                      onClick={() => {
                        void createLeague();
                      }}
                      processing={state.creating}
                    >
                      {createLeagueText}
                    </ActionButton>
                  </div>
                }
                secondaryActions={
                  <button
                    className={mobileActionButtonClassNames.secondary}
                    onClick={() => setWizardStep(2)}
                    type="button"
                  >
                    {i18n.t("common.back", { ns: "ui", defaultValue: "Back" })}
                  </button>
                }
              />
            </div>
          </MobileContentHero>
        ) : null}
      </m.div>
    </AnimatePresence>
  );
};

export default NewLeague;
